import type { ReactNode } from "react";
import { CONTACT_EMAIL, LegalPage, Section } from "./LegalPage";

/**
 * One page listing every way to reach the operator.
 *
 * Ad networks, hosts, and payment processors all check for a reachable abuse route, and
 * so do the people who most need one. Every route lands in the same inbox — the subject
 * lines exist so an urgent report can be told apart from a billing question at a glance,
 * not to route to different places.
 */
export function Contact() {
  return (
    <LegalPage title="Contact" updated="August 4, 2026">
      <p>
        Every route below reaches{" "}
        <span className="text-zinc-100">{CONTACT_EMAIL}</span>; the subject lines just help
        us sort urgent mail from everything else.
      </p>

      <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-4">
        <h2 className="mb-1 text-base font-semibold text-red-300">
          Report content urgently
        </h2>
        <p className="text-zinc-300">
          Material depicting a <span className="text-zinc-100">minor</span>, or any person
          who has <span className="text-zinc-100">not consented</span> to its creation or
          distribution, is removed on report while we review. Include a link to the
          material.
        </p>
        <p className="mt-2">
          <Route subject="Urgent content report">Urgent content report</Route>
        </p>
      </div>

      <Section title="General">
        Questions, feedback, or anything that does not fit below.
        <p className="mt-1">
          <Route subject="goonlords enquiry">General enquiry</Route>
        </p>
      </Section>

      <Section title="Abuse and other reports">
        Harassment, spam, security issues, or content that breaches our{" "}
        <a href="/terms" className="text-[var(--color-accent)] hover:underline">Terms</a>{" "}
        without being urgent in the sense above.
        <p className="mt-1">
          <Route subject="Abuse report">Abuse report</Route>
        </p>
      </Section>

      <Section title="Copyright">
        Notices of claimed infringement and counter-notifications. Please read the{" "}
        <a href="/dmca" className="text-[var(--color-accent)] hover:underline">
          DMCA policy
        </a>{" "}
        first — a notice missing a required element takes longer to action.
        <p className="mt-1">
          <Route subject="DMCA notice">DMCA notice</Route>
        </p>
      </Section>

      <Section title="Records requests">
        Requests relating to our{" "}
        <a href="/2257" className="text-[var(--color-accent)] hover:underline">
          18 U.S.C. §2257 statement
        </a>
        .
        <p className="mt-1">
          <Route subject="2257 records request">2257 records request</Route>
        </p>
      </Section>

      <Section title="Privacy">
        Access, deletion, or any question about what we hold — see the{" "}
        <a href="/privacy" className="text-[var(--color-accent)] hover:underline">
          Privacy Policy
        </a>
        .
        <p className="mt-1">
          <Route subject="Privacy request">Privacy request</Route>
        </p>
      </Section>

      <Section title="Law enforcement">
        Legal process and preservation requests. Please include the requesting agency, a
        callback, and the legal basis.
        <p className="mt-1">
          <Route subject="Law enforcement request">Law enforcement request</Route>
        </p>
      </Section>

    </LegalPage>
  );
}

function Route({ subject, children }: { subject: string; children: ReactNode }) {
  return (
    <a
      href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`}
      className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-edge)] bg-[var(--color-panel-2)] px-2.5 py-1.5 text-sm text-zinc-200 transition-colors hover:border-[var(--color-accent)]/60"
    >
      {children}
      <span className="text-zinc-500">→ {CONTACT_EMAIL}</span>
    </a>
  );
}
